import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, MessageCircle, Info } from "lucide-react";
import { SiteHeader, AgendamentoSection, SegaMeSection, SiteFooter } from "@/components/site-chrome";
import { getPostBySlug, type BlogPost } from "@/lib/blog-data";

export const Route = createFileRoute("/blog/$slug")({
  loader: ({ params }) => {
    const post = getPostBySlug(params.slug);
    if (!post) throw notFound();
    return post;
  },
  head: ({ loaderData }) => {
    const title = loaderData
      ? `${loaderData.title} | Dra. Rafaela Sales`
      : "Blog | Dra. Rafaela Sales";
    const description = loaderData?.metaDescription ?? "Conteúdo educativo sobre cirurgia cardíaca.";
    const url = loaderData
      ? `https://folha-check-helper.lovable.app/blog/${loaderData.slug}`
      : "https://folha-check-helper.lovable.app/blog";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        ...(loaderData ? [{ property: "og:image", content: loaderData.image }] : []),
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  notFoundComponent: () => (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <SiteHeader />
      <main className="mx-auto flex max-w-3xl flex-1 flex-col items-center justify-center px-6 py-24 text-center">
        <h1 className="font-display text-4xl text-foreground">Artigo não encontrado</h1>
        <p className="mt-4 text-sm text-muted-foreground">
          O conteúdo que você procura não está disponível.
        </p>
        <Link
          to="/"
          hash="blog"
          className="mt-8 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.15em] text-primary"
        >
          <ArrowLeft className="h-4 w-4" /> Voltar ao blog
        </Link>
      </main>
      <SiteFooter />
    </div>
  ),
  errorComponent: () => (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <SiteHeader />
      <main className="mx-auto flex max-w-3xl flex-1 flex-col items-center justify-center px-6 py-24 text-center">
        <h1 className="font-display text-4xl text-foreground">Algo deu errado</h1>
        <Link
          to="/"
          hash="blog"
          className="mt-8 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.15em] text-primary"
        >
          <ArrowLeft className="h-4 w-4" /> Voltar ao blog
        </Link>
      </main>
      <SiteFooter />
    </div>
  ),
  component: BlogArticle,
});

function BlogArticle() {
  const post = Route.useLoaderData() as BlogPost;

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <SiteHeader />

      <main className="flex-1">
        {/* Hero do artigo */}
        <section className="relative overflow-hidden bg-secondary/40">
          <img
            src={post.image}
            alt=""
            aria-hidden="true"
            className="absolute inset-y-0 right-0 hidden h-full w-1/2 object-cover opacity-20 md:block"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/40" />
          <div className="relative mx-auto max-w-6xl px-6 py-16 md:py-24">
            <p className="text-xs uppercase tracking-[0.3em] text-rose">{post.category}</p>
            <h1 className="mt-4 max-w-3xl font-sans font-bold text-3xl leading-tight text-foreground sm:text-4xl md:text-5xl">
              {post.title}
            </h1>
          </div>
        </section>

        {/* Conteúdo */}
        <section className="bg-deep">
          <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 md:py-20">
            <article className="rounded-2xl border border-border/60 bg-card p-6 shadow-lg sm:p-10 md:p-14">
              <Link
                to="/"
                hash="blog"
                className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-[0.15em] text-primary transition-colors hover:text-deep"
              >
                <ArrowLeft className="h-4 w-4" /> Voltar ao blog
              </Link>

              <div className="mt-8 space-y-6">
                {post.content.map((block, i) => {
                  if (block.type === "h") {
                    return (
                      <h2
                        key={i}
                        className="font-sans font-bold text-2xl leading-tight text-foreground"
                      >
                        {block.text}
                      </h2>
                    );
                  }
                  if (block.type === "p") {
                    return (
                      <p key={i} className="text-[1.02rem] leading-relaxed text-muted-foreground">
                        {block.text}
                      </p>
                    );
                  }
                  if (block.type === "list") {
                    return (
                      <ul key={i} className="space-y-2.5 pl-1">
                        {block.items.map((item, j) => (
                          <li key={j} className="flex gap-3 text-[1.02rem] leading-relaxed text-muted-foreground">
                            <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-rose" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    );
                  }
                  if (block.type === "image") {
                    return (
                      <img
                        key={i}
                        src={post.image}
                        alt={post.title}
                        loading="lazy"
                        className="my-2 aspect-[16/9] w-full rounded-xl object-cover"
                      />
                    );
                  }
                  // note
                  return (
                    <p
                      key={i}
                      className="flex items-start gap-3 rounded-xl bg-secondary/60 p-4 text-sm italic leading-relaxed text-muted-foreground"
                    >
                      <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      {block.text}
                    </p>
                  );
                })}
              </div>

              {/* CTA dentro do artigo */}
              <div className="mt-12 rounded-2xl bg-rose/15 p-7 text-center sm:p-9">
                <p className="font-sans font-bold text-lg uppercase tracking-[0.08em] text-foreground">
                  Cuide da sua saúde cardiovascular com orientação especializada
                </p>
                <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-muted-foreground">
                  Agende uma avaliação para entender seu caso com segurança e orientação individualizada.
                </p>
                <a
                  href="/#agendamento"
                  className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-primary px-7 py-3.5 text-xs font-medium uppercase tracking-[0.18em] text-primary-foreground shadow-sm transition-all hover:bg-deep hover:shadow-md"
                >
                  <MessageCircle className="h-4 w-4" /> Agendar avaliação
                </a>
              </div>
            </article>
          </div>
        </section>

        <AgendamentoSection />
        <SegaMeSection />
      </main>

      <SiteFooter />
    </div>
  );
}
